import { format, parseISO } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type {
  ProjectAggregate,
  StackedDayAggregate,
} from '@/features/reports/utils/aggregateTasks';
import { formatTooltipDuration } from '@/features/reports/utils/aggregateTasks';

interface HoursBarChartProps {
  data: StackedDayAggregate[];
  projects: ProjectAggregate[];
}

export function HoursBarChart({ data, projects }: HoursBarChartProps) {
  const maxTotal = Math.max(...data.map((d) => d.total), 1);
  const yMax = Math.ceil(maxTotal);
  const ticks = Array.from({ length: yMax + 1 }, (_, i) => i);

  return (
    <div data-testid="hours-bar-chart" className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
          barCategoryGap="30%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(date: string) => format(parseISO(date), 'MMM dd')}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
            tickLine={{ stroke: 'hsl(var(--border))' }}
          />
          <YAxis
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
            tickLine={{ stroke: 'hsl(var(--border))' }}
            tickFormatter={(value: number) => `${value}h`}
            domain={[0, yMax]}
            ticks={ticks}
          />
          <Tooltip
            cursor={{ fill: 'hsl(var(--muted))' }}
            content={({ active, payload, label }) => {
              if (!active || !payload || payload.length === 0 || !label) return null;
              const total = payload.reduce((sum, item) => sum + (item.value as number), 0);
              return (
                <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-sm">
                  <p className="font-medium text-foreground">
                    {format(parseISO(label as string), 'MMM dd, yyyy')}
                  </p>
                  <div className="mt-1 space-y-0.5">
                    {payload
                      .filter((item) => (item.value as number) > 0)
                      .map((item) => (
                        <div key={String(item.name)} className="flex items-center gap-2">
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-muted-foreground">
                            {item.name}: {formatTooltipDuration(item.value as number)}
                          </span>
                        </div>
                      ))}
                  </div>
                  <p className="mt-1 border-t pt-1 text-foreground">
                    Total: {formatTooltipDuration(total)}
                  </p>
                </div>
              );
            }}
          />
          {projects.map((project) => (
            <Bar
              key={project.projectId}
              dataKey={project.projectId}
              name={project.projectName}
              stackId="a"
              fill={project.color ?? 'hsl(var(--primary))'}
              radius={[0, 0, 0, 0]}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
